import React from "react";
import { Box, Button, Typography, LinearProgress, styled } from "@mui/material";
import { BackArrowIcon } from "../../icons/BackArrowIcon";
import type { MSHatData } from "../../../types";

interface HSPHeaderProps {
  onClose: () => void;
  hat: MSHatData;
  progress?: number;
  lightMode?: boolean;
}

const HeaderContainer = styled(Box, {
  label: "HSPHeaderContainer",
})(() => ({
  display: "flex",
  flexDirection: "column",
}));

const HeaderContent = styled(Box, {
  label: "HSPHeaderContent",
})(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(2),
  padding: theme.spacing(3),
}));

const ExitButton = styled(Button, {
  label: "HSPExitButton",
  shouldForwardProp: (prop) => prop !== "lightMode",
})<{ lightMode?: boolean }>(({ theme, lightMode }) => ({
  color: lightMode ? "#1c252e" : "#ffffff",
  border: `1px solid rgba(145, 158, 171, 0.32)`,
  borderRadius: theme.spacing(1),
  padding: theme.spacing(1, 1.5),
  minWidth: 64,
  height: 36,
  fontSize: "14px",
  fontWeight: 700,
  textTransform: "none",
  "&:hover": {
    backgroundColor: lightMode
      ? "rgba(0, 0, 0, 0.04)"
      : "rgba(255, 255, 255, 0.08)",
  },
}));

const HeaderInfo = styled(Box, {
  label: "HSPHeaderInfo",
})(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const TitleRow = styled(Box, {
  label: "HSPTitleRow",
})(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  width: "100%",
}));

const HatInfoSection = styled(Box, {
  label: "HSPHatInfo",
})(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginLeft: "auto",
}));

const HatImage = styled("img", {
  label: "HSPHatImage",
})(() => ({
  width: 32,
  height: 32,
  borderRadius: "50%",
  objectFit: "cover",
  boxShadow: "0px 3px 3px 0px rgba(255, 255, 255, 0.12)",
}));

const StyledLinearProgress = styled(LinearProgress, {
  label: "HSPProgress",
})(() => ({
  height: 8,
  borderRadius: 16,
  backgroundColor: "rgba(145, 158, 171, 0.32)",
  "& .MuiLinearProgress-bar": {
    background: "linear-gradient(170.138deg, #FFD666 0%, #FFAB00 100%)",
    borderRadius: 16,
  },
}));

const PrioritiesIcon = ({ color }: { color: string }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <rect
      x="3"
      y="3"
      width="7"
      height="7"
      rx="1.5"
      stroke={color}
      strokeWidth="2"
    />
    <rect
      x="14"
      y="3"
      width="7"
      height="7"
      rx="1.5"
      stroke={color}
      strokeWidth="2"
    />
    <rect
      x="3"
      y="14"
      width="7"
      height="7"
      rx="1.5"
      stroke={color}
      strokeWidth="2"
    />
    <rect
      x="14"
      y="14"
      width="7"
      height="7"
      rx="1.5"
      stroke={color}
      strokeWidth="2"
    />
  </svg>
);

export const HSPHeader = ({
  onClose,
  hat,
  progress = 0,
  lightMode = false,
}: HSPHeaderProps) => {
  const textColor = lightMode ? "#637381" : "#ffffff";
  const hatNameColor = lightMode ? "#1c252e" : "#ffffff";

  return (
    <HeaderContainer>
      <HeaderContent>
        <ExitButton
          variant="outlined"
          lightMode={lightMode}
          startIcon={
            <BackArrowIcon
              width={20}
              height={20}
              color={lightMode ? "#1c252e" : "#ffffff"}
            />
          }
          onClick={onClose}
        >
          Exit
        </ExitButton>

        <HeaderInfo>
          <TitleRow>
            <PrioritiesIcon color={textColor} />
            <Typography
              variant="h4"
              sx={{
                color: textColor,
                fontFamily: "Proxima Nova",
                fontWeight: 700,
                fontSize: "24px",
                lineHeight: "36px",
                flex: 1,
              }}
            >
              High School Priorities
            </Typography>

            <HatInfoSection>
              {hat.displayImage?.url && (
                <HatImage
                  src={
                    hat.displayImage.sizes?.hatCard?.url || hat.displayImage.url
                  }
                  alt={hat.displayName}
                />
              )}
              <Typography
                sx={{
                  fontFamily: "Proxima Nova",
                  fontWeight: 600,
                  fontSize: "14px",
                  lineHeight: "22px",
                  color: hatNameColor,
                }}
              >
                {hat.displayName}
              </Typography>
            </HatInfoSection>
          </TitleRow>

          <StyledLinearProgress variant="determinate" value={progress} />
        </HeaderInfo>
      </HeaderContent>
    </HeaderContainer>
  );
};
